
import { Log, UserManagerSettings } from "oidc-client";
import { PreCertUser } from "../../Shared/Models/PreCertUser";
import { GetFullPathName, PreCertPaths } from "../../Shared/Utility/PreCertLocationHelper";
import { configService } from "../Config/ConfigService";
import AuthSessionStorage from "./AuthSessionStorage";
import UserManagerSingleton from "./UserManagerSingleton";
import { AccessTokenExpiringNotificationTime } from "../../Auth/SessionTimeout";

export interface IAuthService {
    signinRedirectCallback: () => Promise<string>,
    logout: () => Promise<void>,
    isAuthenticated: () => boolean,
    signinRedirect: (_emailPrefill: string, _redirectUrl: string) => void,
    staffSigninRedirect: () => void,
    signinSilentCallback: () => void,
    createSigninRequest: () => void,
    setOnBehalfOf: (preCertUserId: string | null) => void,
    getOnBehalfOf: () => string,
    getAccessToken: () => Promise<string>,
    currentUser: PreCertUser
}
export default class AuthService implements IAuthService {

    userManager?: UserManagerSingleton = undefined;
    authUrl?: string;
    clientId?: string;
    errorPage: string = GetFullPathName(PreCertPaths.ErrorPage);
    onBehalfOfHeader: string = "oboPreCertUserId";

    currentUser: PreCertUser;

    initialize = async () => {
        var configuration = await configService.getConfiguration();
        this.authUrl = configuration.PreCertIdentity.Authority;
        this.clientId = configuration.PreCertIdentity.ClientId;

        let settings : UserManagerSettings = {
            authority: this.authUrl,
            client_id: this.clientId,
            redirect_uri: configuration.PreCertIdentity.RedirectUri,
            post_logout_redirect_uri: configuration.PreCertIdentity.PostLogoutRedirectUri,
            response_type: "code",
            automaticSilentRenew: false,
            accessTokenExpiringNotificationTime: AccessTokenExpiringNotificationTime,
            loadUserInfo: false,
            scope: configuration.PreCertIdentity.Scope,
            userStore: AuthSessionStorage.getInitUserStore(configuration)
        };

        this.userManager = UserManagerSingleton.getInstance(settings);
        
        // Logger
        Log.logger = console;
        Log.level = Log.DEBUG;
        this.userManager.getOidcUserManager().events.addUserLoaded(() => {
            if (window.location.href.indexOf("signin-oidc") !== -1) {
                this.navigateToScreen();
            }
        });

        this.userManager.getOidcUserManager().events.addSilentRenewError((e: any) => {
            console.error("silent renew error", e.message);
        });
    }


    signinRedirectCallback = async () => {
        await this.userManager.getOidcUserManager().signinRedirectCallback();
        return localStorage.getItem("redirectUri");
    };


    getUser = async () => {
        const user = await this.userManager.getOidcUserManager().getUser();
        if (!user) {
            return await this.userManager.getOidcUserManager().signinRedirectCallback();
        }
        return user;
    };

    getAccessToken = async () => {
        var user = await this.getUser();
        return user && user.access_token;
    }

    setOnBehalfOf = (preCertUserId: string | null) => {
        if (preCertUserId) {
            AuthSessionStorage.getStorageMedia().setItem(this.onBehalfOfHeader, preCertUserId);
        }
    }

    getOnBehalfOf = () => AuthSessionStorage.getStorageMedia().getItem(this.onBehalfOfHeader);

    parseJwt = (token: string) => {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace("-", "+").replace("_", "/");
        return JSON.parse(window.atob(base64));
    };

    signinRedirect = (emailPrefill: string = null, redirectUri: string = null) => {
        if (redirectUri) {
            localStorage.setItem("redirectUri", redirectUri);
        }
        if (AuthSessionStorage.getStorageMedia().getItem(this.onBehalfOfHeader)) {
            AuthSessionStorage.getStorageMedia().removeItem(this.onBehalfOfHeader);
        }

        this.userManager.getOidcUserManager().signinRedirect({ login_hint: emailPrefill });
    };

    staffSigninRedirect = () => {
        localStorage.setItem("redirectUri", window.location.href);
        this.userManager.getOidcUserManager().signinRedirect({ acr_values: "idp:aad" });
    };

    navigateToScreen = () => {
        window.location.replace("/en/dashboard");
    };


    isAuthenticated = () => {
        console.log('authenticating...');
        const oidcStorage = AuthSessionStorage.getOidcSession();
        console.log(`oidc.user:${this.authUrl}:${this.clientId}: ${oidcStorage}`);

        return (!!oidcStorage && !!oidcStorage.access_token)
    };

    signinSilent = () => {
        this.userManager.getOidcUserManager().signinSilent()
            .then((user: any) => {
                console.log("signed in", user);
            })
            .catch((err: any) => {
                console.log(err);
            });
    };

    signinSilentCallback = () => {
        this.userManager.getOidcUserManager().signinSilentCallback();
    };

    createSigninRequest = () => {
        return this.userManager.getOidcUserManager().createSigninRequest();
    };

    logout = async () => {
        const user = await this.userManager.getOidcUserManager().getUser();

        await this.userManager.getOidcUserManager().signoutRedirect({
            id_token_hint: user?.id_token
        });

        await this.userManager.getOidcUserManager().clearStaleState();
    };
}